<template>
  <div>
    <v-data-table
      v-model:page="page"
      :sort-by="sortBy"
      item-value="orderId"
      :headers="headers"
      :items="sortedOrders"
      :items-per-page="itemsPerPage"
      :items-length="sortedOrders.length"
      :custom-key-sort="customKeySort"
      :loading="isLoading"
      :loading-text="t('Loading Content')"
      :no-data-text="t('No data available')"
      @update:sort-by="onUpdateSortBy"
      hide-default-footer
      mobile-breakpoint="800"
    >
      <template #top>
        <ConfirmationDialog
          v-model="showDeleteDialog"
          :title="
            t('Delete - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :message="t('Are you sure you want to delete this Order?')"
          :delete-button="true"
          @confirm="deleteItemConfirm"
          @cancel="closeDeleteDialog"
        />
        <StatusChangeDialog
          v-model="showStatusChangeDialog"
          :title="
            t('Status management - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :statuses="ordersStatus"
          :order="order"
          :data-loading="dataLoading"
          :save-in-progress="saveInProgress"
          :error-on-save="errorOnSave"
          class="dialog"
          @cancel="closeStatusManagementDialog"
          @save="statusManagementSave"
        />
        <OrderTestChangeDialog
          v-model="showOrderTestChangeDialog"
          :title="
            t('Order test change - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :tests="orderFormTests"
          :order="order"
          :data-loading="dataLoading"
          :save-in-progress="saveInProgress"
          :error-on-save="errorOnSave"
          @update-order="ordersStore.order = $event"
          @cancel="closeOrderTestChangeDialog"
          @save="orderTestChangeSave"
        />
        <OrderNotificationDialog
          v-model="showNotificationDialog"
          :title="
            t('Order notifications - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :data-loading="dataLoading"
          :order-notifications="orderNotifications"
          :notifications="notifications"
          :save-in-progress="saveInProgress"
          :error-on-save="errorOnSave"
          :permission="permission"
          @cancel="closeOrderNotificationDialog"
          @save="orderNotificationSave"
        />

        <ReportManagementDialog
          :model-value="showReportManagementDialog"
          :title="
            t('Report management - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :statuses="ordersStatus"
          :order="order"
          :data-loading="dataLoading"
          :save-in-progress="saveInProgress"
          :error-on-save="errorOnSave"
          :reports="orderReports"
          @cancel="closeReportManagementDialog"
          @save="reportManagementSave"
        />
        <ReportDownloadDialog
          v-model="showReportDownloadDialog"
          :title="
            t('Report download - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :reports="orderReports"
          :loaded-value="getCurrentLoadedValue"
          :data-loading="dataLoading"
        />
        <ConsentDownloadDialog
          v-model="showConsentDownloadDialog"
          :title="
            t('Consent download - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :data-loading="dataLoading"
        />
        <OrderViewDialog
          v-model="showOrderViewDialog"
          :title="
            t('View Order - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :reports="supportingFiles"
          :loaded-value="getCurrentSfLoadedValue"
          :data-loading="dataLoading"
          :order="order"
        />
        <SampleReceptionDialog
          v-model="showSampleReceptionDialog"
          :title="
            t('Sample reception - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :reports="supportingFiles"
          :loaded-value="getCurrentSfLoadedValue"
          :data-loading="dataLoading"
          :statuses="ordersStatus"
          :order="order"
          @cancel="closeSampleReceptionDialog"
          @save="sampleReceptionSave"
        />
        <OrderShareDialog
          v-model="showOrderShareDialog"
          :title="
            t('Share Order - Order {orderId}', {
              orderId: selectedOrderId.toString(),
            })
          "
          :data-loading="dataLoading"
          :max-width="'800'"
          :order="{ orderId: selectedOrderId }"
          :order-shareholders="currentOrderUsers"
          @share-with-email="addOrderUser"
          @delete-email="deleteOrderUser"
          @save="showOrderShareDialog = false"
          @cancel="showOrderShareDialog = false"
        />
      </template>
      <template #item.orderDate="{ item }">
        {{ getLocalTime(item.orderDate) }}
      </template>
      <template #item.patientName="{ item }">
        {{ item.patientFirstName }} {{ item.patientLastName }}
      </template>
      <template #item.patientDateOfBirth="{ item }">
        {{ formatDisplayDate(item.patientDateOfBirth) }}
      </template>
      <template #item.formPanelName="{ item }">
        {{ t(item.formPanelName) }}
      </template>
      <template #item.statusId="{ item }">
        <span class="mr-2">{{ t(getStatusName(item.statusId)) }}</span>
        <v-tooltip
          location="bottom"
          attach="#approot"
        >
          <template #activator="{ props: activatorProps }">
            <span
              v-if="getStatusShowTooltip(item.statusId)"
              v-bind="activatorProps"
              dark
              class="text-white"
              tabindex="0"
            >
              <v-icon
                v-if="getStatusShowTooltip(item.statusId)"
                color="primary"
              >
                mdi-information
              </v-icon>
            </span>
          </template>
          <span>{{ t(getStatusTooltip(item.statusId)) }}</span>
        </v-tooltip>
      </template>


      <template #item.sharedBy="{ item }">
        {{ item.ownerFirstName }} {{ item.ownerLastName }}
      </template>
      <template #item.actions="{ item }">
        <OrderGridActionEdit :order="item"/>
        <OrderGridActionView
          :order="item"
          @viewOrder="viewOrder(item)"
        />
        <OrderGridActionReportDownload
          v-if="hasReports(item)"
          @click="reportDownload(item)"
        />
        <OrderGridActionSampleReception
          v-if="hasSampleReceptionPermission()"
          @click="sampleReception(item)"
        />
        <OrderGridActionReportManagement
          v-if="hasOrderReportPermission() && hasRoleReportRelease"
          @click="reportManagement(item)"
        />
        <v-menu
          v-if="hasMoreOptions(item)"
          class="rounded-lg"
          :offset="[0, 4]"
          transition="slide-y-transition"
          attach="#approot"
          location="bottom left"
        >
          <template #activator="{ props: activatorProps }">
            <Button
              small
              color="ma-1 text-primary"
              :title="t('More options')"
              :icon-btn="true"
              :icon-with-background="true"
              v-bind="activatorProps"
            >
              <v-icon size="small">
                mdi-dots-vertical
              </v-icon>
            </Button>
          </template>
          <v-list density="compact">
            <v-list-item
              v-if="orderNotificationPermission(item) !== PERMISSION.frontendCheck.none"
              @click="orderNotification(item)"
            >
              <template #prepend>
                <v-icon size="x-small">
                  mdi-bell
                </v-icon>
              </template>
              {{ t('Order notifications') }}
            </v-list-item>

            <v-list-item
              v-if="hasOrderUserPermission(item)"
              @click="shareOrder(item)"
            >
              <template #prepend>
                <v-icon size="x-small">
                  mdi-share-variant-outline
                </v-icon>
              </template>
              {{ t('Share order') }}
            </v-list-item>

            <v-list-item
              v-if="hasOrderReportPermission() && !hasRoleReportRelease"
              @click="reportManagement(item)"
            >
              <template #prepend>
                <v-icon size="x-small">
                  mdi-file-upload
                </v-icon>
              </template>
              {{ t('Report management') }}
            </v-list-item>

            <v-list-item
              v-if="hasStatusManagementPermission()"
              @click="statusManagement(item)"
            >
              <template #prepend>
                <v-icon size="x-small">
                  mdi-format-list-checks
                </v-icon>
              </template>
              {{ t('Status management') }}
            </v-list-item>

            <v-list-item @click="consentDownload(item)">
              <template #prepend>
                <v-icon size="x-small">
                  mdi-download
                </v-icon>
              </template>
              {{ t('Consent download') }}
            </v-list-item>

            <v-list-item
              v-if="hasChangeOrderTestPermission(item)"
              @click="changeOrderedTest(item)"
            >
              <template #prepend>
                <v-icon size="x-small">
                  mdi-swap-horizontal
                </v-icon>
              </template>
              {{ t('Change ordered test') }}
            </v-list-item>

            <v-list-item
              v-if="hasDeleteOrderPermission(item)"
              @click="deleteItem(item)"
            >
              <template #prepend>
                <v-icon size="x-small">
                  mdi-trash-can
                </v-icon>
              </template>
              {{ t('Delete order') }}
            </v-list-item>

            <v-list-item
              v-else-if="hasRemoveFromSharedPermission(item)"
              @click="removeSharedAccess(item)"
            >
              <template #prepend>
                <v-icon size="x-small">
                  mdi-account-remove
                </v-icon>
              </template>
              {{ t('Remove from shared') }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <Pagination
      v-if="pageCount > 1"
      v-model="page"
      :page-count="pageCount"
    />
  </div>
</template>
<script setup lang="ts">
import {computed, onMounted, reactive, ref, toRefs, watchEffect, inject} from 'vue';
import Button from "@/components/input-components/Button.vue";
import ConfirmationDialog from "@/components/dialogs/ConfirmationDialog.vue";
import NotificationMessageService from "@/services/notificationmessage.service";
import OrderGridActionEdit from "@/components/orders/OrderGridActionEdit.vue";
import OrderGridActionReportDownload from "@/components/orders/OrderGridActionReportDownload.vue";
import OrderGridActionSampleReception from "@/components/orders/OrderGridActionSampleReception.vue";
import OrderGridActionReportManagement from "@/components/orders/OrderGridActionReportManagement.vue";
import OrderGridActionView from "@/components/orders/OrderGridActionView.vue";
import OrderNotificationDialog from "@/components/dialogs/OrderNotificationDialog.vue";
import OrderShareDialog from "@/components/dialogs/OrderShareDialog.vue";
import Pagination from "@/components/common/Pagination.vue";
import {ORDER_STATUS, PERMISSION} from "@/utils/constants";
import ReportDownloadDialog from "@/components/dialogs/ReportDownloadDialog.vue";
import ConsentDownloadDialog from "@/components/dialogs/ConsentDownloadDialog.vue";
import ReportManagementDialog from "@/components/dialogs/ReportManagementDialog.vue";
import OrderViewDialog from "@/components/dialogs/OrderViewDialog.vue";
import SampleReceptionDialog from "@/components/dialogs/SampleReceptionDialog.vue";
import StatusChangeDialog from "@/components/dialogs/StatusChangeDialog.vue";
import OrderTestChangeDialog from "@/components/dialogs/OrderTestChangeDialog.vue";
import {promisedTimeout} from "@/utils";
import {OrderUsersService} from "@/services/orderusers.service";
import {usePermissionsService} from '@/composables/usePermissionsService';
import {useOrdersStore} from '@/stores/orders';
import {useReportsStore} from '@/stores/reports';
import {usePhysiciansStore} from '@/stores/physicians';
import {useFormStore} from '@/stores/form';
import {useSupportingFilesStore} from '@/stores/supportingFiles';
import {useOrderNotificationStore} from '@/stores/orderNotification';
import {useI18n} from 'vue-i18n';
import {useDateHelper} from '@/composables/useDateHelper';
import {useVue2Sort} from "@/composables/useVue2Sort";
import {useLocalizationHelper} from "@/composables/useLocalizationHelper";
import {authKey} from '@/plugins/authentication';
import {OrderStatus} from "@/entities/order-status";
import {Order} from "@/entities/order";
import {ReportDeletePayload, ReportCreatePayload, ReportUI} from "@/entities/report";
import {OrderUser} from "@/entities/order-user";
import {UpdateNotificationPayload} from "@/entities/notification";

const {getPanelName} = useLocalizationHelper();
const {sortDate, sortCalendarDate, getLocalTime, formatDisplayDate} = useDateHelper();

// Props
const props = defineProps({
  orderIdPreFilter: {
    type: String,
    default: ''
  }
})

// i18n
const {t, locale} = useI18n();

// Stores
const ordersStore = useOrdersStore();
const reportsStore = useReportsStore();
const physiciansStore = usePhysiciansStore();
const formStore = useFormStore();
const supportingFilesStore = useSupportingFilesStore();
const orderNotificationStore = useOrderNotificationStore();

// Services
const permissionsService = usePermissionsService();
const orderUserService = new OrderUsersService();

// Reactive state
const state = reactive({
  showDeleteDialog: false,
  showStatusChangeDialog: false,
  showOrderTestChangeDialog: false,
  showNotificationDialog: false,
  showReportManagementDialog: false,
  showReportDownloadDialog: false,
  showConsentDownloadDialog: false,
  showSampleReceptionDialog: false,
  showOrderViewDialog: false,
  showOrderShareDialog: false,
  selectedOrderId: -1,
  page: 1,
  itemsPerPage: 20,
  dataLoading: false,
  saveInProgress: false,
  errorOnSave: false,
  currentOrderUsers: [] as OrderUser[],
});

const {
  showDeleteDialog,
  showStatusChangeDialog,
  showOrderTestChangeDialog,
  showNotificationDialog,
  showReportManagementDialog,
  showReportDownloadDialog,
  showConsentDownloadDialog,
  showSampleReceptionDialog,
  showOrderViewDialog,
  showOrderShareDialog,
  selectedOrderId,
  page,
  itemsPerPage,
  dataLoading,
  saveInProgress,
  errorOnSave,
  currentOrderUsers,
} = toRefs(state);

const { sortBy, onUpdateSortBy } = useVue2Sort({ key: 'orderId', order: 'desc' })

const permission = ref(PERMISSION.frontendCheck.none);

const auth = inject(authKey);

// Computed helpers

const authUserId = computed(() => auth.value?.sub);
const orderFormId = computed((): string => ordersStore.getOrder.form?.formConfig?.uniqueId ?? '');
const hasRoleReportRelease = computed(() => permissionsService.hasRoleReportRelease);
const contentType = computed((): string => ordersStore.getGridContentType);

type TableMode = "my" | "shared";

const columnDefs = [
  { key: "orderDate",          titleKey: "Order date",       sortable: true,  sort: sortDate },
  { key: "orderId",            titleKey: "Order ID",         sortable: true },
  { key: "referenceId",        titleKey: "External ID",      sortable: true }, // only my
  { key: "cegatId",            titleKey: "CeGaT ID",         sortable: true },
  { key: "patientName",        titleKey: "Patient",          sortable: true },
  { key: "patientDateOfBirth", titleKey: "Patient d.o.b",    sortable: true,  sort: sortCalendarDate },
  { key: "formPanelName",      titleKey: "Test",             sortable: true },
  { key: "statusId",           titleKey: "Status",           sortable: true },
  { key: "sharedBy",           titleKey: "Shared By",        sortable: true }, // only shared
];

const widthsMatrix: Record<TableMode, Record<string, string>> = {
  my: {
    orderDate:          "11%",
    orderId:            "8%",
    referenceId:        "8%",   // unique
    cegatId:            "7%",
    patientName:        "16%",
    patientDateOfBirth: "11.5%",
    formPanelName:      "11%",
    statusId:           "13%",
    actions:            "14.5%",
  },
  shared: {
    orderDate:          "11%",
    orderId:            "7%",
    cegatId:            "8%",
    patientName:        "14%",
    patientDateOfBirth: "10.5%",
    formPanelName:      "12%",
    statusId:           "13%",
    sharedBy:           "10%",  // unique
    actions:            "14.5%",
  },
};

interface ExtendedHeader {
  title: string;
  key: string;
  sortable?: boolean;
  sort?: (a: any, b: any) => number;
  width?: string;
}

const headers = computed<ExtendedHeader[]>(() => {
  const mode: TableMode = contentType.value === "My Orders" ? "my" : "shared";
  const widths = widthsMatrix[mode];

  const cols = columnDefs
    .filter(col => mode === "my" ? col.key !== "sharedBy" : col.key !== "referenceId")
    .map<ExtendedHeader>(col => {
      const header: ExtendedHeader = {
        title: t(col.titleKey),
        key: col.key,
        sortable: col.sortable ?? false,
      };

      if (col["sort"]) {
        header.sort = col["sort"];
      }

      if (widths[col.key]) {
        header.width = widths[col.key];
      }

      return header;
    });

  cols.push({
    title: t("Actions"), key: "actions", sortable: false, width: widths.actions,
  });

  return cols;
});


const filteredOrders = computed(() =>
  orders.value
    .map(o => ({
      ...o,
      patientName: `${o.patientFirstName ?? ''} ${o.patientLastName ?? ''}`.trim(),
    }))
);

const sortedOrders = computed(() => {
  const arr = [...(filteredOrders.value || [])]
  if (!sortBy.value.length) return arr

  const {key, order} = sortBy.value[0]
  const sorter = customKeySort[key]

  if (typeof sorter === 'function') {
    return arr.sort((a, b) => {
      const result = sorter(a, b)
      return order === 'desc' ? -result : result
    })
  }
  return arr
})

const pageCount = computed(() =>
  Math.ceil(sortedOrders.value.length / state.itemsPerPage)
)

const order = computed(() => ordersStore.getOrder);
const orderFormTests = computed(() => formStore.getFormTests);
const ordersStatus = computed((): OrderStatus[] => ordersStore.getOrdersStatus);
const notifications = computed(() => orderNotificationStore.notifications);
const orderNotifications = computed(() => ordersStore.getOrderNotifications);
const orderReports = computed(() => reportsStore.getReports);
const getCurrentLoadedValue = computed(() => reportsStore.getLoadedValue);
const supportingFiles = computed(() => supportingFilesStore.getSupportingFiles);
const getCurrentSfLoadedValue = computed(() => supportingFilesStore.getLoadedValue);
const getPhysicianId = computed(() => physiciansStore.getPhysician.userId);
const isLoading = computed(() => ordersStore.getLoadingCount > 0);
const searchCegatId = computed(() => ordersStore.getSearchCegatId);
const searchOrderId = computed(() => ordersStore.getSearchOrderId);
const isSearchingByCegatId = computed((): boolean => searchCegatId.value !== "" && searchCegatId.value !== null);
const isSearchingByOrderId = computed((): boolean => searchOrderId.value !== "" && searchOrderId.value !== null);

const orders = computed((): Order[] => {
  let myOrders = contentType.value === 'My Orders'
  let physicianId = getPhysicianId.value
  let currentPhysicianId = physicianId && physicianId.trim() !== '' ? physicianId : authUserId.value

  return ordersStore.getOrders.filter((order: Order) =>
    isSearchingByCegatId.value ||
    isSearchingByOrderId.value ||
    (myOrders ? order.doctorId === currentPhysicianId : order.doctorId !== currentPhysicianId)
  );
});

watchEffect(() => {
  if (filteredOrders.value.length) {
    sortBy.value = [{key: 'orderId', order: 'desc'}]
  } else {
    sortBy.value = []
  }
})

function getStatusName(id: string): string {
  const status = ordersStore.getOrdersStatus.find((s: OrderStatus) => s.id === id);
  return status?.name ?? "";
}

function getStatusTooltip(id: string): string {
  const status = ordersStore.getOrdersStatus.find((s: OrderStatus) => s.id === id);
  return t(status?.tooltip ?? "") ?? "";
}

function getStatusShowTooltip(id: string): boolean | undefined {
  const status = ordersStore.getOrdersStatus.find((s: OrderStatus) => s.id === id);
  return status?.showTooltip;
}

async function fetchOrder() {
  if (state.selectedOrderId !== -1) {
    await ordersStore.fetchOrder([state.selectedOrderId, getPhysicianId.value]);
  }
}

async function fetchOrders() {
  if (permissionsService.hasOrderReadPermission && !permissionsService.hasOrderReadAllPermission) {
    await ordersStore.fetchOrders(true);
  }
}

async function fetchOrdersStatus() {
  await ordersStore.fetchOrdersStatus();
  await fetchOrders();
}

async function fetchOrderNotifications() {
  if (state.selectedOrderId !== -1) {
    await Promise.all([
      orderNotificationStore.fetchNotifications(),
      ordersStore.fetchOrderNotifications(state.selectedOrderId),
    ]);
  }
}

// ConfirmationDialog
function deleteItem(item: Order): void {
  state.selectedOrderId = item.orderId;
  state.showDeleteDialog = true;
}

const targetUnshareUserId = computed<string | null>(() => {
  if (auth.value?.isExternalUser) {
    return authUserId.value ?? null;
  }

  return physiciansStore.getPhysician.userId || null;
});

async function removeSharedAccess(item: Order): Promise<void> {
  const targetId = targetUnshareUserId.value;

  if (!targetId && contentType.value === 'Shared Orders') {
    console.warn('removeSharedAccess: shared context but target is null');
    return;
  }

  try {
    const users = await orderUserService.getOrderUsers(item.orderId);
    const targetUser = users.find(u => u.doctorId === targetId);

    if (!targetUser) {
      NotificationMessageService.showError(t('Active doctor is not a participant of this order.'));
      return;
    }

    await orderUserService.deleteOrderUser(targetUser.orderUserId);

    ordersStore.removeOrderFromCurrentList(item.orderId);
    NotificationMessageService.showSuccess(t('Access removed successfully.'));
  } catch (error) {
    NotificationMessageService.showError(t('Failed to remove shared access.'));
  }
}

async function deleteItemConfirm(): Promise<void> {
  await ordersStore.deleteOrder([state.selectedOrderId, getPhysicianId.value]);
  closeDeleteDialog();
}

function closeDeleteDialog(): void {
  state.showDeleteDialog = false;
  state.selectedOrderId = -1;
}

// OrderNotificationDialog
function orderNotification(item: Order): void {
  permission.value = orderNotificationPermission(item);
  if (permission.value === PERMISSION.frontendCheck.none) return;

  prepareDialogLoad(item);
  const openDialogTimeout = setTimeout(() => {
    state.showNotificationDialog = true;
  }, 200);

  fetchOrderNotifications().then(() => {
    clearTimeout(openDialogTimeout);
    state.showNotificationDialog = true;
    state.dataLoading = false;
  });
}

async function orderNotificationSave(payload: UpdateNotificationPayload): Promise<void> {
  prepareDialogSave();

  let errorOnSave = false;
  for (let i = 0; i < payload.remove.length; i++) {
    const result = await ordersStore.deleteOrderNotification({
      orderId: state.selectedOrderId,
      notificationId: payload.remove[i].notificationId,
    });
    if (!errorOnSave && !result) errorOnSave = true;
    if (i % 5 === 0) await promisedTimeout(300);
  }

  for (let i = 0; i < payload.add.length; i++) {
    const result = await ordersStore.postOrderNotification({
      orderId: state.selectedOrderId,
      notification: payload.add[i],
    });
    if (!errorOnSave && !result) errorOnSave = true;
    if (i % 5 === 0) await promisedTimeout(300);
  }

  if (!errorOnSave) {
    closeOrderNotificationDialog();
    NotificationMessageService.showSuccess(t("Order notification settings saved!"));
  }

  state.errorOnSave = errorOnSave;
  state.saveInProgress = false;
}

function closeOrderNotificationDialog(): void {
  state.showNotificationDialog = false;
  state.selectedOrderId = -1;
}

// OrderShareDialog
function shareOrder(item: Order): void {
  prepareDialogLoad(item);
  const openDialogTimeout = setTimeout(() => {
    state.showOrderShareDialog = true;
  }, 200);

  orderUserService.getOrderUsers(item.orderId)
    .then(response => {
      state.currentOrderUsers = response;
      clearTimeout(openDialogTimeout);
      state.showOrderShareDialog = true;
    })
    .finally(() => {
      state.dataLoading = false;
    });
}

async function addOrderUser(payload: { orderId: number, email: string }): Promise<void> {
  state.dataLoading = true;
  state.saveInProgress = true;
  try {
    await orderUserService.postOrderUsers(payload.orderId, payload.email);
    state.saveInProgress = false;
    state.currentOrderUsers = await orderUserService.getOrderUsers(payload.orderId);
    NotificationMessageService.showSuccess(t("Shared user has been added successful."));
  } catch {
    NotificationMessageService.showError(t("There was an error adding this email."));
  } finally {
    state.dataLoading = false;
    state.saveInProgress = false;
  }
}

async function deleteOrderUser(payload: { data: { orderUserId: number, orderId: number } }): Promise<void> {
  state.dataLoading = true;
  state.saveInProgress = true;
  try {
    await orderUserService.deleteOrderUser(payload.data.orderUserId);
    state.saveInProgress = false;
    state.currentOrderUsers = await orderUserService.getOrderUsers(payload.data.orderId);
    NotificationMessageService.showSuccess(t("Shared user has been deleted successful."));
  } catch {
    NotificationMessageService.showError(t("Please try again later."));
  } finally {
    state.dataLoading = false;
    state.saveInProgress = false;
  }
}

// ReportManagementDialog
async function reportManagement(item: Order) {
  prepareDialogLoad(item);
  state.showReportManagementDialog = false;

  const openDialogTimeout = setTimeout(() => {
    state.showReportManagementDialog = true;
  }, 200);

  await Promise.all([
    reportsStore.fetchOrderReports(state.selectedOrderId),
    fetchOrder(),
  ]);

  clearTimeout(openDialogTimeout);
  state.showReportManagementDialog = true;
  state.dataLoading = false;
}

async function deleteReports(reports: ReportDeletePayload[]): Promise<boolean> {
  let errorOnSave = false;
  for (let i = 0; i < reports.length; i++) {
    const result = await reportsStore.deleteReport(
      reports[i].orderId,
      reports[i].orderFileId
    );
    if (!errorOnSave && !result) errorOnSave = true;
    if (i % 5 === 0) await promisedTimeout(300);
  }
  return errorOnSave;
}

async function addNewReports(reports: ReportUI[]): Promise<void> {
  for (const report of reports) {
    if (!(report.file instanceof File)) {
      console.warn("Skip report without file", report);
      continue;
    }

    const payload: ReportCreatePayload = {
      file: report.file,
      comment: report.comment ?? ''
    };

    const created = await reportsStore.insertOrderReport(
      state.selectedOrderId,
      payload
    );

    if (!created) {
      NotificationMessageService.showError(t('Failed to upload file') + ': ' + report.fileName);
      throw new Error("Upload failed");
    }

    report.orderFileId = created.orderFileId;
    report.fileId = created.fileId;
    report.fileName = created.fileName;
    report.orderId = created.orderId;
    report.comment = created.comment;
    report.delete = created.delete;
    report.isNew = false;
    report.file = undefined;
    report.originalComment = report.comment;
  }
}

async function reportDownload(item: Order) {
  prepareDialogLoad(item);
  state.dataLoading = true;

  const openDialogTimeout = setTimeout(() => {
    state.showReportDownloadDialog = true;
  }, 200);

  try {
    await reportsStore.fetchOrderReports(state.selectedOrderId)
    clearTimeout(openDialogTimeout)
    state.showReportDownloadDialog = true
    state.dataLoading = false
  } catch (e) {
    state.dataLoading = false
    console.error('Error on reports download', e)
  }
}

async function updateReports(update: any) {
  let success = true
  for (const report of update) {
    success = success && await reportsStore.updateOrderReport(
      state.selectedOrderId,
      report
    );
  }
  return success
}

async function reportManagementSave(args: any) {
  prepareDialogSave();

  try {
    if (args.remove.length > 0) {
      const delError = await deleteReports(args.remove);
      if (delError) {
        NotificationMessageService.showError(t("Failed to delete reports"));
        state.errorOnSave = true;
        return;
      }
    }

    if (args.add.length > 0) {
      await addNewReports(args.add);
    }

    if (args.update.length > 0) {
      const updateErr = await updateReports(args.update);
      if (updateErr) {
        NotificationMessageService.showError(t("Failed to update comments"));
        state.errorOnSave = true;
        return;
      }
    }

    if (args.statusChanged && args.statusId) {
      ordersStore.order.statusId = args.statusId;
    }

    const putOrderErr = await ordersStore.putOrder([false, getPhysicianId.value]);
    if (putOrderErr) {
      NotificationMessageService.showError(t("Failed to save order"));
      state.errorOnSave = true;
      return;
    }

    NotificationMessageService.showSuccess(t("Report Information Saved!"));
    closeReportManagementDialog();

  } catch (error: any) {
    state.errorOnSave = true;
    NotificationMessageService.showError(t("Error during Report Management Save"), error);
  } finally {
    state.saveInProgress = false;
  }
}

function closeReportManagementDialog() {
  state.showReportManagementDialog = false;
  state.selectedOrderId = -1;
  state.errorOnSave = false;
}

// StatusChangeDialog
function statusManagement(item: Order) {
  prepareDialogLoad(item);

  const openDialogTimeout = setTimeout(() => {
    showStatusChangeDialog.value = true;
  }, 200);

  fetchOrder()
    .then(() => {
      clearTimeout(openDialogTimeout);
      showStatusChangeDialog.value = true;
      dataLoading.value = false;
    })
    .finally(() => {
      dataLoading.value = false;
    });
}

function statusManagementSave(): void {
  prepareDialogSave();
  ordersStore.putOrder([true, getPhysicianId.value]).then(res => {
    if (res === false) {
      closeStatusManagementDialog();
    } else {
      state.errorOnSave = true;
    }
    state.saveInProgress = false;
  });
}

function closeStatusManagementDialog() {
  state.showStatusChangeDialog = false;
  state.selectedOrderId = -1;
}

// OrderTestChangeDialog
async function changeOrderedTest(item: Order) {
  prepareDialogLoad(item);
  state.dataLoading = true;

  const openDialogTimeout = setTimeout(() => {
    state.showOrderTestChangeDialog = true;
  }, 200);

  try {
    await fetchOrder();
    await formStore.fetchFormTests(orderFormId.value);
    clearTimeout(openDialogTimeout);
    state.showOrderTestChangeDialog = true;
  } catch (error) {
    console.error('Error while changing ordered test:', error);
  } finally {
    state.dataLoading = false;
  }
}

function orderTestChangeSave() {
  prepareDialogSave();
  ordersStore.putOrder([true, getPhysicianId.value])
    .then((res) => {
      if (res === false) {
        closeOrderTestChangeDialog();
      } else {
        state.errorOnSave = true;
      }
      state.saveInProgress = false;
    });
}

function closeOrderTestChangeDialog() {
  state.showOrderTestChangeDialog = false;
  state.selectedOrderId = -1;
}

// SampleReceptionDialog
async function sampleReception(item: Order) {
  prepareDialogLoad(item);
  state.dataLoading = true;

  const openDialogTimeout = setTimeout(() => {
    state.showSampleReceptionDialog = true;
  }, 200);

  try {
    await supportingFilesStore.fetchOrderReports(state.selectedOrderId)
    await fetchOrder()
    clearTimeout(openDialogTimeout);
    state.showSampleReceptionDialog = true;
  } catch (error) {
    console.error("Error during sample reception:", error);
  } finally {
    state.dataLoading = false;
  }
}

function closeSampleReceptionDialog() {
  state.showSampleReceptionDialog = false;
  state.selectedOrderId = -1;
}

async function sampleReceptionSave(): Promise<void> {
  prepareDialogSave();
  const res = await ordersStore.putOrder([true, getPhysicianId.value]);
  if (res === false) {
    closeSampleReceptionDialog();
  } else {
    state.errorOnSave = true;
  }
  state.saveInProgress = false;
}

// OrderViewDialog
async function orderViewDialog(item: Order) {
  prepareDialogLoad(item);

  const openDialogTimeout = setTimeout(() => {
    showOrderViewDialog.value = true;
  }, 200);

  try {
    await supportingFilesStore.fetchOrderReports(state.selectedOrderId);
    await fetchOrder();
  } catch (error) {
    console.error("Error upon fetching OrderReports:", error);
  } finally {
    clearTimeout(openDialogTimeout);
    showOrderViewDialog.value = true;
    dataLoading.value = false;
  }
}

async function viewOrder(item: Order) {
  await orderViewDialog(item);
}

// ConsentDownloadDialog
async function consentDownload(item: Order) {
  prepareDialogLoad(item);
  state.dataLoading = true;

  const openDialogTimeout = setTimeout(() => {
    state.showConsentDownloadDialog = true;
  }, 200);

  try {
    await fetchOrder();
    clearTimeout(openDialogTimeout);
    state.showConsentDownloadDialog = true;
  } catch (error) {
    console.error('Error during consent download:', error);
  } finally {
    state.dataLoading = false;
  }
}

// PREPARE DIALOG
function prepareDialogLoad(item: Order) {
  state.dataLoading = true;
  state.errorOnSave = false;
  state.selectedOrderId = item.orderId;
}

function prepareDialogSave() {
  state.saveInProgress = true;
  state.errorOnSave = false;
}

// PERMISSIONS
function hasDeleteOrderPermission(item: Order): boolean {
  return (
    contentType.value === 'My Orders' && (
      permissionsService.hasOrderDeleteAllPermission ||
      (
        item.statusId === ORDER_STATUS.draft &&
        permissionsService.hasOrderDeletePermission &&
        item.doctorId === authUserId.value
      )
    )
  )
}

function hasOrderUserPermission(item: Order): boolean {
  return (
    item.statusId !== ORDER_STATUS.draft &&
    (item.doctorId === authUserId.value || permissionsService.hasOrderUpdateAllPermission)
  )
}

function orderNotificationPermission(item: Order): string {
  if (item.statusId !== ORDER_STATUS.draft) {
    const hasReadPermissions = permissionsService.hasNotificationReadPermission || permissionsService.hasNotificationReadAllPermission;
    if (hasReadPermissions) {
      const hasReadPermissions = (
        (permissionsService.hasOrderCreatePermission || permissionsService.hasOrderCreateAllPermission) &&
        (permissionsService.hasOrderDeletePermission || permissionsService.hasOrderDeleteAllPermission)
      ) || permissionsService.hasRoleExternalSales;

      return hasReadPermissions ? PERMISSION.frontendCheck.readWrite : PERMISSION.frontendCheck.readOnly;
    }
  }
  return PERMISSION.frontendCheck.none;
}

function hasOrderReportPermission(): boolean {
  return (
    permissionsService.hasOrderReportCreateAllPermission &&
    permissionsService.hasOrderReportDeleteAllPermission &&
    permissionsService.hasOrderReportReadAllPermission
  )
}

function hasReports(item: Order): boolean {
  return item.hasReports === true;
}

function hasSampleReceptionPermission(): boolean {
  return (
    permissionsService.hasOrderUpdateAllPermission &&
    permissionsService.hasOrderSupportingFileReadAllPermission
  )
}

function hasStatusManagementPermission(): boolean {
  return permissionsService.hasOrderUpdateAllPermission;
}

function hasChangeOrderTestPermission(item: Order): boolean {
  return (
    permissionsService.hasOrderUpdateAllPermission &&
    item.statusId === ORDER_STATUS.orderSubmitted
  )
}

function hasRemoveFromSharedPermission(item: Order): boolean {
  return (
    contentType.value === 'Shared Orders' &&
    targetUnshareUserId.value !== null &&
    item.doctorId !== targetUnshareUserId.value
  );
}

function hasMoreOptions(item: Order): boolean {
  return (
    orderNotificationPermission(item) !== PERMISSION.frontendCheck.none ||
    hasOrderUserPermission(item) ||
    hasOrderReportPermission() ||
    hasStatusManagementPermission() ||
    hasChangeOrderTestPermission(item) ||
    hasDeleteOrderPermission(item) ||
    hasRemoveFromSharedPermission(item)
  )
}

const customKeySort = {
  patientName: (a, b) => (a.patientName ?? '').localeCompare(b.patientName ?? ''),
  orderDate: (a, b) => sortDate(a.orderDate, b.orderDate),
  orderId: (a, b) => a.orderId - b.orderId,
  referenceId: (a, b) => (a.referenceId ?? '').localeCompare(b.referenceId ?? ''),
  cegatId: (a, b) => (a.cegatId ?? '').localeCompare(b.cegatId ?? ''),
  patientDateOfBirth: (a, b) => sortCalendarDate(a.patientDateOfBirth, b.patientDateOfBirth),
  formTestName: (a, b) => (getPanelName(a, locale.value) ?? '').localeCompare(getPanelName(b, locale.value) ?? ''),
  statusId: (a, b) => getStatusName(a.statusId)?.localeCompare(getStatusName(b.statusId) ?? '') ?? 0,
  sharedBy: (a, b) => `${a.ownerFirstName || ''} ${a.ownerLastName || ''}`.toLowerCase().localeCompare(`${b.ownerFirstName || ''} ${b.ownerLastName || ''}`.toLowerCase())
};

onMounted(() => {
  if (props.orderIdPreFilter && props.orderIdPreFilter !== '') {
    ordersStore.searchInput = props.orderIdPreFilter
  }

  fetchOrdersStatus()
})

defineExpose({
  selectedOrderId,
  t,
});
</script>
